import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  getEngineRootStyle,
  titleStyle,
  instructionsStyle,
  primaryBtn,
  secondaryBtn,
  feedbackCorrect,
  feedbackIncorrect,
} from './shared/engineTheme';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { CIRCUIT_PATH_ONE_SHOT } from './shared/engineInteractionOneShots';
import { isTapFirstLessonUi } from './shared/lessonUiMode';
import { shouldSuppressActivityTitle } from './shared/guidedPromptUtils';

/**
 * Simple “fill the gaps” circuit path for young learners.
 *
 * Content:
 * {
 *   instructions?, prompt?,
 *   slots: Array<
 *     | { id, role: 'battery'|'bulb'|'switch'|'label', icon?: string, label?: string }
 *     | { id, role: 'gap', options: string[], correct: string }
 *   >,
 *   pieceLabels?: Record<pieceId, { label?: string, icon?: string }>,
 *   victory?, explanation?,
 * }
 *
 * Guided lesson shell (`onRegisterLessonFooterActions` + gaps): **Check** in the lesson footer; the **bulb glow**
 * and gold path ring stay off until Check (or Show answer). In-engine Check is hidden in that mode. Tapping gaps
 * still cycles pieces; changing gaps after a wrong Check clears pending feedback until the next Check.
 */

const DEFAULT_ICONS = {
  battery: '🔋',
  bulb: '💡',
  switch: '🔘',
  label: '→',
  ground: '⏚',
};

export default function CircuitPathEngine({
  title = 'Complete the path',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_circuit_path',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  lessonUiMode = 'default',
  guidedContext,
  onRegisterLessonFooterActions,
}) {
  const singleSurface = guidedContext?.singleSurface === true;
  const stepTitle = typeof guidedContext?.stepTitle === 'string' ? guidedContext.stepTitle : '';
  const suppressActivityTitle = shouldSuppressActivityTitle({ stepTitle, activityTitle: title });
  const tapFirst = isTapFirstLessonUi(lessonUiMode);
  const compactPath = tapFirst && singleSurface;
  const shellLessonDefer =
    typeof onRegisterLessonFooterActions === 'function' &&
    (Array.isArray(content.slots) ? content.slots.some((s) => s?.role === 'gap') : false);

  const slots = Array.isArray(content.slots) ? content.slots : [];
  const pieceLabels = content.pieceLabels || {};

  const gapIds = useMemo(
    () => slots.filter((s) => s && s.role === 'gap' && s.id).map((s) => s.id),
    [slots],
  );

  const initialGapState = useMemo(() => {
    const m = {};
    for (const s of slots) {
      if (s?.role === 'gap' && s.id) {
        const opts = Array.isArray(s.options) ? s.options : ['wire'];
        m[s.id] = opts[0] || 'wire';
      }
    }
    return m;
  }, [slots]);

  const [gapState, setGapState] = useState(() => ({ ...initialGapState }));
  const [evalVisible, setEvalVisible] = useState(() => !shellLessonDefer);
  const [completed, setCompleted] = useState(false);
  const [showCelebration, celebrate] = useCelebration();
  const [a11y, setA11y] = useState('');
  const [wrongCheck, setWrongCheck] = useState(false);

  const getExplanation = useCallback(() => content.explanation, [content.explanation]);

  const guided = useGuidedHints({
    activityId,
    hints,
    oneShotInteractionHint: CIRCUIT_PATH_ONE_SHOT,
    explanation,
    getExplanation,
    isActivityComplete: completed,
    enabled: guidedHintsEnabled,
  });
  const { noteUserAction, noteCorrectAnswer, noteIncorrectAnswer, resetSession } = guided;

  const allCorrect = useMemo(() => {
    for (const s of slots) {
      if (s?.role !== 'gap') continue;
      const cur = gapState[s.id];
      if (cur !== s.correct) return false;
    }
    return gapIds.length > 0;
  }, [slots, gapState, gapIds.length]);

  const showEval = !shellLessonDefer || evalVisible;
  const displayBulbLit = allCorrect && (showEval || completed);

  useEffect(() => {
    setEvalVisible(!shellLessonDefer);
  }, [activityId, shellLessonDefer]);

  const runCheck = useCallback(() => {
    if (completed) return;
    noteUserAction();
    if (shellLessonDefer) setEvalVisible(true);
    if (allCorrect) {
      setCompleted(true);
      noteCorrectAnswer();
      celebrate();
      setA11y('The lamp is on — the path is complete.');
      trackMiniAppEvent.interaction(activityId, 'correct');
      trackMiniAppEvent.completed(activityId);
      onComplete?.({ activityId, engine: 'circuit_path', perfect: true });
    } else {
      setWrongCheck(true);
      noteIncorrectAnswer();
      setA11y('Not yet — every gap needs the right piece.');
      trackMiniAppEvent.interaction(activityId, 'incorrect');
    }
  }, [
    completed,
    noteUserAction,
    shellLessonDefer,
    allCorrect,
    noteCorrectAnswer,
    celebrate,
    activityId,
    onComplete,
    noteIncorrectAnswer,
  ]);

  const revealAnswer = useCallback(() => {
    if (completed) return;
    noteUserAction();
    const next = { ...gapState };
    for (const s of slots) {
      if (s?.role === 'gap' && s.id && s.correct != null) next[s.id] = s.correct;
    }
    setGapState(next);
    setEvalVisible(true);
    setWrongCheck(false);
  }, [completed, noteUserAction, slots, gapState]);

  useEffect(() => {
    if (!onRegisterLessonFooterActions) return undefined;
    if (completed || !shellLessonDefer) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    onRegisterLessonFooterActions({
      checkAnswer: runCheck,
      checkAnswerDisabled: false,
      showAnswer: revealAnswer,
    });
    return () => onRegisterLessonFooterActions(null);
  }, [onRegisterLessonFooterActions, completed, shellLessonDefer, runCheck, revealAnswer]);

  const cycleGap = (gapId) => {
    if (completed) return;
    noteUserAction();
    if (shellLessonDefer) {
      setEvalVisible(false);
      setWrongCheck(false);
    }
    const slot = slots.find((s) => s.id === gapId && s.role === 'gap');
    if (!slot) return;
    const opts = Array.isArray(slot.options) ? slot.options : ['wire'];
    setGapState((prev) => {
      const cur = prev[gapId] ?? opts[0];
      const idx = Math.max(0, opts.indexOf(cur));
      const next = opts[(idx + 1) % opts.length];
      return { ...prev, [gapId]: next };
    });
    onProgress?.({ activityId, engine: 'circuit_path', phase: 'cycle', gapId });
  };

  const resetBoard = () => {
    if (completed) return;
    resetSession();
    setGapState({ ...initialGapState });
    setWrongCheck(false);
    if (shellLessonDefer) setEvalVisible(false);
    onProgress?.({ activityId, engine: 'circuit_path', phase: 'reset' });
  };

  const renderPiece = (pieceId) => {
    const meta = pieceLabels[pieceId] || {};
    const icon = meta.icon || (pieceId === 'wire' ? '〰️' : pieceId === 'empty' ? '○' : '▫️');
    return (
      <span style={{ fontSize: compactPath ? 22 : 26, lineHeight: 1 }} aria-hidden>
        {icon}
      </span>
    );
  };

  const prompt =
    content.prompt || 'Tap each dashed gap until the wire path looks complete, then Check.';

  const wrongStripVisible = wrongCheck && !completed && (!shellLessonDefer || evalVisible);

  return (
    <div
      style={{
        ...getEngineRootStyle({ singleSurface }),
        position: 'relative',
        ...(guided.contentReserveStyle || {}),
        ...(compactPath ? { overflow: 'hidden', minHeight: 0 } : {}),
      }}
    >
      <CelebrationOverlay visible={showCelebration} message={content.victory?.message || 'It lights up!'} />
      <A11yAnnouncement message={a11y} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {!suppressActivityTitle ? (
        <h2 style={{ ...titleStyle, ...(compactPath ? { margin: '0 0 6px' } : {}) }}>{title}</h2>
      ) : null}
      {compactPath ? (
        <p
          style={{
            ...instructionsStyle,
            fontSize: guided.isNarrow ? 13 : 14,
            lineHeight: 1.35,
            margin: '0 0 8px',
            fontWeight: 600,
          }}
        >
          {prompt}
        </p>
      ) : (
        <>
          {content.instructions && <p style={instructionsStyle}>{content.instructions}</p>}
          <p style={{ ...instructionsStyle, fontWeight: 700, marginTop: content.instructions ? undefined : 0 }}>
            {prompt}
          </p>
        </>
      )}

      <div
        role="group"
        aria-label="Circuit pieces"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'stretch',
          justifyContent: 'center',
          gap: compactPath ? 6 : 8,
          padding: compactPath ? '10px 8px' : '14px 10px',
          borderRadius: compactPath ? 12 : 16,
          border: `2px solid ${displayBulbLit ? 'rgba(250, 204, 21, 0.65)' : 'var(--color-border)'}`,
          background: displayBulbLit ? 'rgba(250, 204, 21, 0.1)' : 'var(--color-bg)',
          marginBottom: compactPath ? 8 : 12,
        }}
      >
        {slots.map((s) => {
          if (!s || !s.id) return null;
          if (s.role === 'gap') {
            const val = gapState[s.id];
            return (
              <button
                key={s.id}
                type="button"
                disabled={completed}
                onClick={() => cycleGap(s.id)}
                aria-label={`Gap ${s.label || s.id}. Tap to change piece.`}
                style={{
                  minWidth: compactPath ? 52 : 56,
                  minHeight: compactPath ? 52 : 56,
                  borderRadius: compactPath ? 12 : 14,
                  border: '2px dashed var(--color-primary)',
                  background: 'rgba(99,102,241,0.08)',
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: compactPath ? 4 : 6,
                  cursor: completed ? 'default' : 'pointer',
                }}
              >
                {renderPiece(val)}
                <span
                  style={{
                    fontSize: compactPath ? 9 : 10,
                    fontWeight: 800,
                    marginTop: 4,
                    color: 'var(--color-text)',
                  }}
                >
                  {pieceLabels[val]?.label || val}
                </span>
              </button>
            );
          }
          const icon = s.icon || DEFAULT_ICONS[s.role] || '▪️';
          const isBulb = s.role === 'bulb';
          return (
            <div
              key={s.id}
              style={{
                minWidth: compactPath ? 52 : 56,
                minHeight: compactPath ? 52 : 56,
                borderRadius: compactPath ? 12 : 14,
                border: '2px solid var(--color-border)',
                background: 'var(--color-bg-card)',
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: compactPath ? 4 : 6,
                boxShadow:
                  isBulb && displayBulbLit
                    ? '0 0 18px rgba(250, 204, 21, 0.85), inset 0 0 12px rgba(250, 204, 21, 0.35)'
                    : undefined,
              }}
            >
              <span style={{ fontSize: compactPath ? 24 : 28, lineHeight: 1 }} aria-hidden>
                {icon}
              </span>
              {s.label ? (
                <span
                  style={{
                    fontSize: compactPath ? 9 : 10,
                    fontWeight: 700,
                    marginTop: 4,
                    color: 'var(--color-text)',
                  }}
                >
                  {s.label}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: compactPath ? 6 : 8 }}>
        {!shellLessonDefer ? (
          <button
            type="button"
            style={{ ...primaryBtn, flex: '1 1 140px', minHeight: compactPath ? 44 : 48 }}
            onClick={runCheck}
            disabled={completed}
          >
            Check
          </button>
        ) : null}
        <button
          type="button"
          style={{
            ...secondaryBtn,
            flex: shellLessonDefer ? '1 1 100%' : '1 1 120px',
            width: shellLessonDefer ? '100%' : undefined,
            minHeight: compactPath ? 44 : 48,
            fontSize: compactPath ? 14 : 16,
          }}
          onClick={resetBoard}
          disabled={completed}
        >
          Reset
        </button>
      </div>

      {wrongStripVisible ? (
        <div
          style={{
            ...feedbackIncorrect,
            marginTop: compactPath ? 8 : 10,
            ...(compactPath
              ? { fontSize: guided.isNarrow ? 12 : 13, lineHeight: 1.45 }
              : {}),
          }}
        >
          Try again — power needs an unbroken loop to the bulb and back.
        </div>
      ) : null}
      {completed ? (
        <div
          style={{
            ...feedbackCorrect,
            marginTop: compactPath ? 6 : 10,
            fontSize: compactPath ? 14 : undefined,
          }}
        >
          {content.victory?.message || 'Electrons have a complete highway — the bulb shines!'}
        </div>
      ) : null}
    </div>
  );
}
